import { memo, useMemo, useState } from 'react';
import { IconButton, InputAdornment, List, ListItem, ListItemButton, ListItemText } from '@mui/material';
import AddTwoTone from '@mui/icons-material/AddTwoTone';
import SearchTwoTone from '@mui/icons-material/SearchTwoTone';
import StarTwoTone from '@mui/icons-material/StarTwoTone';
import { useFormikContext } from 'formik';
import TextField from '@forms/Components/TextInput';
import { confirmAsync } from '@components/Modals/ConfirmPopup';
import { unsavedChangesMessage } from '@forms/types';
import { BoardQuery } from '@api/Trinium/types';
import { isActiveQuery } from '@api/Trinium/Endpoints/BoardQueries/types';
import { BoardQueryFormFields } from './types';

import styles from './Page.module.css';

interface QueryListProps {
  queries: BoardQuery[];
  selectedQuery: BoardQuery | undefined;
  draftQuery: BoardQuery | undefined;
  onSelect: (query: BoardQuery) => void;
  onCreate: () => void;
}

/**
 * Selectable list of the terminal's editable Board Queries. Lives inside the editor's Formik context so a
 * selection can be refused while the currently open query has unsaved work.
 * Formik context has no selective subscription, so this re-renders on every keystroke in the editor. It reads the
 * one fact it needs and hands the list below a memoized boolean, keeping the list itself off that path.
 */
export default function QueryList(props: QueryListProps) {
  const { dirty } = useFormikContext<BoardQueryFormFields>();

  // An open draft is unsaved work even before it is edited, since a copy arrives complete and savable as it stands
  return (
    <QueryListView
      {...props}
      hasUnsavedWork={dirty || Boolean(props.draftQuery)}
    />
  );
}

/** The draft row's label is the only thing in the list that tracks the form, so it subscribes on its own. */
function DraftQueryName() {
  const { values } = useFormikContext<BoardQueryFormFields>();

  return <span className={styles['query-item-name']}>{values.name || 'New Query'}</span>;
}

/**
 * A query being created has no record to list, so it is shown as a draft row of its own — named after whatever the
 * name field currently holds — to keep the fact that something unsaved is open visible while it is being filled out.
 */
const QueryListView = memo(function QueryListView(props: QueryListProps & { hasUnsavedWork: boolean }) {
  const [searchText, setSearchText] = useState('');

  const filteredQueries = useMemo(
    () => props.queries.filter((query) => query.name.toLowerCase().includes(searchText.toLowerCase())),
    [props.queries, searchText]
  );

  const handleSelect = async (query: BoardQuery) => {
    if (query.id === props.selectedQuery?.id) return;

    if (props.hasUnsavedWork && !(await confirmAsync(unsavedChangesMessage))) return;

    props.onSelect(query);
  };

  return (
    <div className={styles['query-list']}>
      <div className={styles['list-header']}>
        <span className={styles['section-title']}>Board Queries</span>

        <IconButton
          title='Create a New Board Query'
          onClick={props.onCreate}
          className={`${styles['icon-btn']} ${styles['add-btn']}`}
        >
          <AddTwoTone />
        </IconButton>
      </div>

      <div className={styles['search-container']}>
        <TextField
          placeholder='Filter Board Queries'
          className={styles['search-input']}
          value={searchText}
          onChange={(event) => {
            setSearchText(event.target.value);
          }}
          customProps={{
            labelText: '',
            isDirty: false,
            onClear: () => {
              setSearchText('');
            },
            startAdornment: (
              <InputAdornment position='start'>
                <SearchTwoTone />
              </InputAdornment>
            )
          }}
        />
      </div>

      <List
        disablePadding
        className={styles['query-items']}
      >
        {props.draftQuery && (
          <ListItem
            disablePadding
            className={`${styles['query-item']} ${styles.active}`}
          >
            <div className={styles['query-item-btn']}>
              <DraftQueryName />

              <span className={styles['draft-badge']}>Draft</span>
            </div>
          </ListItem>
        )}

        {filteredQueries.length === 0 ? (
          <ListItem className={styles['empty-item']}>
            <ListItemText>{props.queries.length === 0 ? 'No Editable Board Queries' : 'No Matching Board Queries'}</ListItemText>
          </ListItem>
        ) : (
          filteredQueries.map((query) => (
            <ListItem
              key={`edit-query-${String(query.id)}`}
              disablePadding
              className={`${styles['query-item']} ${query.id === props.selectedQuery?.id ? styles.active : ''}`}
            >
              <ListItemButton
                onClick={() => {
                  void handleSelect(query);
                }}
                className={`${styles['query-item-btn']} ${isActiveQuery(query) ? '' : styles.inactive}`}
              >
                <span className={styles['query-item-name']}>{query.name}</span>

                {!isActiveQuery(query) && <span className={styles['disabled-badge']}>Disabled</span>}

                {query.isFavorite && (
                  <StarTwoTone
                    fontSize='small'
                    className={styles.favorited}
                    titleAccess='Favorite Query'
                  />
                )}

                {query.isDefault && <span className={styles['default-badge']}>Default</span>}
              </ListItemButton>
            </ListItem>
          ))
        )}
      </List>
    </div>
  );
});
